import { isSabHandle, isSabUsable, SabMessageChannel, } from '@streamr/tv-engine';
import { getVideoCodecOverride, resolveVideoCodecs, Settings, } from '@streamr/tv-engine/~/settings';
import { proxy, transfer, wrap } from 'comlink';
import { isWebKitBrowser } from '../../utils/browser';
import { orientationTypeToRotationMap } from '../../utils/getIosFrameRotation';
import { getRemoteLogPageConfig } from '../../utils/remoteLogger';
import { createWorker } from '../../utils/workerUtils';
import { isGapDiagnosticsEnabled } from './GapDiagnostics';
import { perfWorkerName } from './perfFlags';
import { collectSceneTransferables } from './types/Scene';
/**
 * Base class for video engines. Owns the lifecycle of the unified
 * `VideoWorker` (which contains player + per-track encoder + composite
 * encoder + decoder) and a separate `TimerWorker`.
 *
 * The previous three-worker split (`VideoPlayerWorker`,
 * `VideoEncoderWorker`, `VideoDecoderWorker`) has been collapsed into one
 * worker so `VideoFrame` objects never cross worker boundaries. See
 * `VideoWorker.ts` for the rationale and the eliminated MessagePort routes.
 */
export class AbstractVideoEngine {
    optimalDelayCallback;
    encodedVideoOutputTrackAddedCallback;
    // Unified video worker (player + encoder + decoder)
    videoWorker;
    videoWorkerProxy;
    // Per-subsystem readiness flags so the legacy "start/stop XWorker"
    // calls continue to make sense after the merge.
    playerStarted = false;
    encoderStarted = false;
    decoderStarted = false;
    timerWorker;
    timerWorkerProxy;
    timerWiredToVideoWorker = false;
    wireTimerPortInFlight;
    pendingDisplayCanvas;
    displayOutputCallback;
    // kept across worker restarts: applied whenever a worker is made
    directOutputPolicy;
    async init(optimalDelayCallback, encodedVideoOutputTrackAddedCallback, displayOutputCallback) {
        this.optimalDelayCallback = optimalDelayCallback;
        this.encodedVideoOutputTrackAddedCallback =
            encodedVideoOutputTrackAddedCallback;
        this.displayOutputCallback = displayOutputCallback;
        await this.onInit();
        console.log(`${this.LOG_PREFIX} init`);
    }
    async destroy() {
        await this.onDestroy();
        if (this.encoderStarted) {
            await this.stopVideoEncoderWorker();
        }
        if (this.decoderStarted) {
            await this.stopVideoDecoderWorker();
        }
        if (this.playerStarted) {
            await this.stopVideoPlayerWorker();
        }
        await this.maybeTerminateVideoWorker();
        this.destroyTimerWorker();
        console.log(`${this.LOG_PREFIX} destroy`);
    }
    async onDestroy() { }
    async onInit() { }
    // ========== Unified video worker bootstrap ==========
    /**
     * Lazily spawn the unified video worker. The three legacy
     * "start*Worker" entry points all funnel through here; we keep them
     * as separate methods so callers retain explicit ordering / readiness
     * semantics without having to know that one worker now hosts all
     * three subsystems.
     */
    async ensureVideoWorker() {
        if (this.videoWorkerProxy)
            return;
        if (!this.optimalDelayCallback) {
            throw new Error('init() must be called before starting workers');
        }
        if (!this.encodedVideoOutputTrackAddedCallback) {
            throw new Error('init() must be called before starting workers');
        }
        this.videoWorker = this.createVideoWorker();
        this.videoWorkerProxy = wrap(this.videoWorker);
        const remoteLogPageConfig = getRemoteLogPageConfig();
        const videoWorkerRemoteLog = remoteLogPageConfig
            ? { ...remoteLogPageConfig, source: 'iPhone:videoWorker' }
            : null;
        await this.videoWorkerProxy.init(proxy(this.optimalDelayCallback), proxy(this.encodedVideoOutputTrackAddedCallback), isGapDiagnosticsEnabled(), videoWorkerRemoteLog, { isWebKit: isWebKitBrowser() }, this.displayOutputCallback
            ? proxy(this.displayOutputCallback)
            : undefined);
        if (this.directOutputPolicy) {
            await this.videoWorkerProxy.setDirectOutputPolicy(this.directOutputPolicy);
        }
        console.log(`${this.LOG_PREFIX} VideoWorker started`);
    }
    async maybeTerminateVideoWorker() {
        if (!this.videoWorker ||
            this.playerStarted ||
            this.encoderStarted ||
            this.decoderStarted) {
            return;
        }
        try {
            await this.videoWorkerProxy?.destroy();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} VideoWorker destroy failed`, error);
        }
        this.videoWorker.terminate();
        this.videoWorker = undefined;
        this.videoWorkerProxy = undefined;
        this.timerWiredToVideoWorker = false;
        console.log(`${this.LOG_PREFIX} VideoWorker terminated`);
    }
    async startVideoPlayerWorker() {
        if (this.playerStarted) {
            console.warn(`${this.LOG_PREFIX} player already started`);
            return;
        }
        await this.ensureVideoWorker();
        await this.wireTimerPort();
        if (this.pendingDisplayCanvas) {
            console.log(`${this.LOG_PREFIX} applying deferred display canvas`);
            const canvas = this.pendingDisplayCanvas;
            this.pendingDisplayCanvas = undefined;
            await this.setDisplayCanvas(canvas);
        }
        this.playerStarted = true;
        console.log(`${this.LOG_PREFIX} player ready`);
    }
    async stopVideoPlayerWorker() {
        this.playerStarted = false;
        await this.maybeTerminateVideoWorker();
        console.log(`${this.LOG_PREFIX} player stopped`);
    }
    async startVideoDecoderWorker() {
        if (this.decoderStarted) {
            console.warn(`${this.LOG_PREFIX} decoder already started`);
            return;
        }
        // Player / encoder / decoder all share the same worker now and are
        // started concurrently from `StreamrAppApi.startWorkersForRole`
        // (Promise.all). `ensureVideoWorker()` is idempotent, so any order
        // is safe.
        await this.ensureVideoWorker();
        this.decoderStarted = true;
        console.log(`${this.LOG_PREFIX} decoder ready`);
    }
    async stopVideoDecoderWorker() {
        this.decoderStarted = false;
        await this.maybeTerminateVideoWorker();
        console.log(`${this.LOG_PREFIX} decoder stopped`);
    }
    async startVideoEncoderWorker() {
        if (this.encoderStarted) {
            console.warn(`${this.LOG_PREFIX} encoder already started`);
            return;
        }
        // Concurrent-safe with startVideoPlayerWorker / startVideoDecoderWorker:
        // `ensureVideoWorker()` / `wireTimerPort()` are both idempotent.
        await this.ensureVideoWorker();
        await this.wireTimerPort();
        // The ?videoCodec=… URL param is lost when the SPA canonicalizes
        // the broadcast URL before a late engine start (e.g. a viewer
        // promoted to guest), so test harnesses can also pin the codec via
        // localStorage.
        const codecOverride = getVideoCodecOverride() ??
            globalThis.localStorage?.getItem('videoCodecOverride');
        if (codecOverride && this.videoWorkerProxy) {
            await this.videoWorkerProxy.setVideoCodecOverride(codecOverride);
        }
        this.encoderStarted = true;
        await this.onVideoEncoderWorkerStarted();
        console.log(`${this.LOG_PREFIX} encoder ready`);
    }
    async onVideoEncoderWorkerStarted() {
        // Subclass hook (e.g. StandardVideoEngine connects the extractor)
    }
    setPerTrackVideoEncodingEnabled(enabled) {
        this.videoWorkerProxy?.setPerTrackEncodingEnabled(enabled);
    }
    async connectEncoderNetworkingPort(handle) {
        if (!this.videoWorkerProxy) {
            throw new Error('VideoWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.videoWorkerProxy.connectEncoderNetworkingPort(handle);
        }
        else {
            await this.videoWorkerProxy.connectEncoderNetworkingPort(transfer(handle, [handle]));
        }
    }
    async connectCompositeNetworkingPort(handle) {
        if (!this.videoWorkerProxy) {
            throw new Error('VideoWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.videoWorkerProxy.connectCompositeNetworkingPort(handle);
        }
        else {
            await this.videoWorkerProxy.connectCompositeNetworkingPort(transfer(handle, [handle]));
        }
    }
    async connectCompositeLowNetworkingPort(handle) {
        if (!this.videoWorkerProxy) {
            throw new Error('VideoWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.videoWorkerProxy.connectCompositeLowNetworkingPort(handle);
        }
        else {
            await this.videoWorkerProxy.connectCompositeLowNetworkingPort(transfer(handle, [handle]));
        }
    }
    async connectDecoderNetworkingPort(handle) {
        if (!this.videoWorkerProxy) {
            throw new Error('VideoWorker not running');
        }
        if (isSabHandle(handle)) {
            await this.videoWorkerProxy.connectDecoderNetworkingPort(handle);
        }
        else {
            await this.videoWorkerProxy.connectDecoderNetworkingPort(transfer(handle, [handle]));
        }
    }
    async stopVideoEncoderWorker() {
        await this.onVideoEncoderWorkerStopping();
        this.encoderStarted = false;
        await this.maybeTerminateVideoWorker();
        console.log(`${this.LOG_PREFIX} encoder stopped`);
    }
    async onVideoEncoderWorkerStopping() { }
    isVideoPlayerWorkerRunning() {
        return this.playerStarted;
    }
    isVideoDecoderWorkerRunning() {
        return this.decoderStarted;
    }
    isVideoEncoderWorkerRunning() {
        return this.encoderStarted;
    }
    setPerTrackVideoBitrate(trackId, bitrate) {
        this.videoWorkerProxy?.setTrackBitrate(trackId, bitrate);
    }
    // ========== Display & playback ==========
    async setDisplayCanvas(canvas) {
        // Gate only on worker presence, not on the `playerStarted` flag.
        // `startVideoPlayerWorker` calls back into this method to apply
        // `pendingDisplayCanvas` BEFORE flipping `playerStarted=true`; gating
        // on `playerStarted` would re-defer that call and the canvas would
        // never reach the worker. `ensureVideoWorker()` awaits `init()`, so
        // the proxy is safe to use as soon as it is set.
        if (!this.videoWorkerProxy) {
            console.log(`${this.LOG_PREFIX} setDisplayCanvas: deferring (worker not ready)`);
            this.pendingDisplayCanvas = canvas;
            return;
        }
        this.pendingDisplayCanvas = undefined;
        const offscreenCanvas = canvas.transferControlToOffscreen();
        await this.videoWorkerProxy.setDisplayCanvas(transfer(offscreenCanvas, [offscreenCanvas]));
        console.log(`${this.LOG_PREFIX} setDisplayCanvas`);
    }
    async setLiveSceneThumbnailCanvasMounted(canvas) {
        console.log(`${this.LOG_PREFIX} setLiveSceneThumbnailCanvasMounted`, canvas ? 'mount' : 'unmount');
        if (canvas) {
            await this.videoWorkerProxy?.setLiveSceneThumbnailCanvasMounted(transfer(canvas, [canvas]));
        }
        else {
            await this.videoWorkerProxy?.setLiveSceneThumbnailCanvasMounted();
        }
    }
    async setReferenceTimes(trackIds, presentationTimeMicros, realTimeMillis) {
        await this.videoWorkerProxy?.setReferenceTimes(trackIds, presentationTimeMicros, realTimeMillis);
    }
    async reportUnderflow(trackIds) {
        await this.videoWorkerProxy?.reportUnderflow(trackIds);
    }
    async setAudioDriven(trackIds, enabled) {
        await this.videoWorkerProxy?.setAudioDriven(trackIds, enabled);
    }
    async connectAudioSyncPort(audioTrackId, handle) {
        await this.videoWorkerProxy?.connectAudioSyncPort(audioTrackId, handle);
    }
    async disconnectAudioSyncPort(audioTrackId) {
        await this.videoWorkerProxy?.disconnectAudioSyncPort(audioTrackId);
    }
    async setAudioVideoMapping(audioTrackId, videoTrackIds) {
        await this.videoWorkerProxy?.setAudioVideoMapping(audioTrackId, videoTrackIds);
    }
    async getClockDiagnostics(trackIds) {
        return ((await this.videoWorkerProxy?.getClockDiagnostics(trackIds)) ?? []);
    }
    async setDirectOutputPolicy(policy) {
        this.directOutputPolicy = policy;
        await this.videoWorkerProxy?.setDirectOutputPolicy(policy);
    }
    async feedEncodedVideoFrame(frame) {
        if (frame.done) {
            console.log(`${this.LOG_PREFIX} feedEncodedVideoFrame done`, {
                frame,
            });
        }
        const transferables = [];
        if (frame.data.byteLength > 0) {
            transferables.push(frame.data.buffer);
        }
        if (frame.extradata.byteLength > 0 &&
            frame.extradata.buffer !== frame.data.buffer) {
            transferables.push(frame.extradata.buffer);
        }
        if (transferables.length > 0) {
            await this.videoWorkerProxy?.feedEncodedVideoFrame(transfer(frame, transferables));
        }
        else {
            await this.videoWorkerProxy?.feedEncodedVideoFrame(frame);
        }
    }
    async handleLeave(userSessionId) {
        console.log(`${this.LOG_PREFIX} handleLeave`, { userSessionId });
        await this.videoWorkerProxy?.handleLeave(userSessionId);
    }
    // ========== Composite encoding ==========
    async startRenderedFrameCapture(timestampOffsetMicros = 0n) {
        if (!this.videoWorkerProxy) {
            throw new Error('VideoWorker not running');
        }
        let resolveEncodedTrack;
        const encodedTrackPromise = new Promise((resolve) => {
            resolveEncodedTrack = resolve;
        });
        await this.videoWorkerProxy.addCompositeEncoder('viewer', resolveVideoCodecs(Settings.viewerStreamVideoCodecs), Settings.viewerStreamVideoBitrate, proxy(async (encodedTrackProxy) => {
            resolveEncodedTrack(encodedTrackProxy);
        }));
        await this.videoWorkerProxy.startRenderedFrameCapture(timestampOffsetMicros);
        const outputTrack = await encodedTrackPromise;
        console.log(`${this.LOG_PREFIX} startRenderedFrameCapture`);
        return outputTrack;
    }
    async stopRenderedFrameCapture() {
        try {
            await this.videoWorkerProxy?.stopRenderedFrameCapture();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} videoWorker.stopRenderedFrameCapture failed`, error);
        }
        console.log(`${this.LOG_PREFIX} stopRenderedFrameCapture`);
    }
    /**
     * The low-quality composite: the live scene downscaled to the low
     * profile, encoded by the "low" role with the viewer codecs at
     * `Settings.lowQualityStreamVideoBitrate`, for the low content partition.
     */
    async startLowRenderedFrameCapture(timestampOffsetMicros = 0n) {
        if (!this.videoWorkerProxy) {
            throw new Error('VideoWorker not running');
        }
        await this.videoWorkerProxy.addCompositeEncoder('low', resolveVideoCodecs(Settings.viewerStreamVideoCodecs), Settings.lowQualityStreamVideoBitrate, proxy(async (_track) => {
            // frames travel to the NetworkingWorker over the low port
        }), {
            maxWidth: Settings.lowQualityStreamVideoWidth,
            maxHeight: Settings.lowQualityStreamVideoHeight,
        });
        await this.videoWorkerProxy.startLowRenderedFrameCapture(timestampOffsetMicros);
        console.log(`${this.LOG_PREFIX} startLowRenderedFrameCapture`);
    }
    async stopLowRenderedFrameCapture() {
        try {
            await this.videoWorkerProxy?.stopLowRenderedFrameCapture();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} videoWorker.stopLowRenderedFrameCapture failed`, error);
        }
        console.log(`${this.LOG_PREFIX} stopLowRenderedFrameCapture`);
    }
    async startRtmpCapture(rtmpBridgePort) {
        if (!this.videoWorkerProxy) {
            throw new Error('VideoWorker not running');
        }
        await this.videoWorkerProxy.addCompositeEncoder('rtmp', Settings.rtmpVideoCodecs, Settings.rtmpVideoBitrate, proxy(async (encodedTrackProxy) => {
            const trackId = await encodedTrackProxy.getTrackId();
            console.log(`${this.LOG_PREFIX} RTMP encoded track available`, { trackId: trackId.slice(0, 8) });
            const listener = async (frame, done) => {
                if (frame && !done) {
                    try {
                        rtmpBridgePort.postMessage(frame, [
                            frame.data.buffer,
                        ]);
                    }
                    catch {
                        // Port may be closed
                    }
                }
                if (done) {
                    rtmpBridgePort.postMessage({ done: true });
                }
            };
            await encodedTrackProxy.setListener(proxy(listener));
        }));
        await this.videoWorkerProxy.startRtmpFrameCapture();
        console.log(`${this.LOG_PREFIX} startRtmpCapture`);
    }
    async stopRtmpCapture() {
        try {
            await this.videoWorkerProxy?.stopRtmpFrameCapture();
        }
        catch (error) {
            console.warn(`${this.LOG_PREFIX} videoWorker.stopRtmpFrameCapture failed`, error);
        }
        console.log(`${this.LOG_PREFIX} stopRtmpCapture`);
    }
    // ========== Scene & display ==========
    async pinVideo(trackId) {
        await this.videoWorkerProxy?.pinVideo(trackId);
    }
    async unpinVideo() {
        await this.videoWorkerProxy?.unpinVideo();
    }
    async togglePinVideo(trackId) {
        await this.videoWorkerProxy?.togglePinVideo(trackId);
    }
    async getVideoAtPosition(x, y) {
        return (await this.videoWorkerProxy?.getVideoAtPosition(x, y)) ?? null;
    }
    async setDisplayScene(config) {
        await this.transferSceneToPlayer('setDisplayScene', config);
    }
    async setLiveScene(config) {
        await this.transferSceneToPlayer('setLiveScene', config);
    }
    async transferSceneToPlayer(method, config) {
        const transferables = collectSceneTransferables(config);
        if (transferables.length > 0) {
            await this.videoWorkerProxy?.[method](transfer(config, transferables));
        }
        else {
            await this.videoWorkerProxy?.[method](config);
        }
    }
    async getActiveTrackIds() {
        return (await this.videoWorkerProxy?.getActiveTrackIds()) ?? [];
    }
    async captureDisplayFrame(width, height) {
        return ((await this.videoWorkerProxy?.captureDisplayFrame(width, height)) ??
            '');
    }
    async captureCompositeFrame(timeoutMs) {
        return this.videoWorkerProxy?.captureCompositeFrame(timeoutMs);
    }
    async setThumbnailRenderingConfig(entries) {
        const transferables = [];
        for (const entry of entries) {
            if (entry.canvas) {
                transferables.push(entry.canvas);
            }
        }
        if (!this.videoWorkerProxy) {
            if (transferables.length > 0) {
                throw new Error('Cannot transfer thumbnail canvases: worker proxy not ready');
            }
            return;
        }
        if (transferables.length > 0) {
            await this.videoWorkerProxy.setThumbnailRenderingConfig(transfer(entries, transferables));
        }
        else {
            await this.videoWorkerProxy.setThumbnailRenderingConfig(entries);
        }
    }
    async addVideoTrack(track) {
        await this.videoWorkerProxy?.addVideoTrack(proxy(track));
    }
    async setExcludeLocalTracksFromMainCanvas(exclude) {
        await this.videoWorkerProxy?.setExcludeLocalTracksFromMainCanvas(exclude);
    }
    setFocusRemoteTrackId(trackId) {
        this.videoWorkerProxy?.setFocusRemoteTrackId(trackId);
    }
    async setScreenOrientation(type, _angle) {
        const canvasRotation = orientationTypeToRotationMap[type] ?? 0;
        await this.videoWorkerProxy?.setCanvasRotation(canvasRotation);
    }
    // ========== TimerWorker lifecycle ==========
    async ensureTimerWorker() {
        if (this.timerWorkerProxy) {
            return;
        }
        console.log(`${this.LOG_PREFIX} creating TimerWorker`);
        this.timerWorker = this.createTimerWorker();
        this.timerWorkerProxy = wrap(this.timerWorker);
        await this.timerWorkerProxy.start();
        console.log(`${this.LOG_PREFIX} TimerWorker created and started`);
    }
    async wireTimerPort() {
        // Called concurrently by parallel start*Worker() invocations
        // (Promise.all in StreamrAppApi.startWorkersForRole). Coalesce via
        // a single in-flight promise so we don't leak an extra SAB ring
        // when player + encoder both start at once.
        if (this.timerWiredToVideoWorker)
            return;
        if (this.wireTimerPortInFlight) {
            return this.wireTimerPortInFlight;
        }
        this.wireTimerPortInFlight = (async () => {
            await this.ensureTimerWorker();
            if (!this.timerWorkerProxy || !this.videoWorkerProxy) {
                return;
            }
            if (isSabUsable()) {
                // 30 fps ticks bypass the main thread via a SAB ring; capacity
                // 64 KiB easily holds many ticks even if the consumer briefly
                // stalls (per-tick payload ≈ 32 bytes).
                const ch = new SabMessageChannel({
                    capacity: 64 * 1024,
                    name: 'timer<->videoworker',
                });
                await this.timerWorkerProxy.connectPort(ch.port1.getHandle());
                await this.videoWorkerProxy.connectTimerPort(ch.port2.getHandle());
            }
            else {
                const channel = new MessageChannel();
                await this.timerWorkerProxy.connectPort(transfer(channel.port1, [channel.port1]));
                await this.videoWorkerProxy.connectTimerPort(transfer(channel.port2, [channel.port2]));
            }
            this.timerWiredToVideoWorker = true;
            console.log(`${this.LOG_PREFIX} TimerWorker port wired to VideoWorker`);
        })();
        try {
            await this.wireTimerPortInFlight;
        }
        finally {
            this.wireTimerPortInFlight = undefined;
        }
    }
    destroyTimerWorker() {
        if (this.timerWorkerProxy) {
            this.timerWorkerProxy.stop();
        }
        this.timerWorker?.terminate();
        this.timerWorkerProxy = undefined;
        this.timerWorker = undefined;
    }
    async getCurrentTime() {
        if (!this.timerWorkerProxy) {
            return performance.now();
        }
        return this.timerWorkerProxy.getCurrentTime();
    }
    // ========== Worker creation ==========
    createVideoWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./VideoWorker.worker.ts', import.meta.url), { type: 'module', name: perfWorkerName('videoWorker') });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./VideoWorker.worker${ext}`, import.meta.url, perfWorkerName('videoWorker'));
    }
    createTimerWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./TimerWorker.worker.ts', import.meta.url), { type: 'module', name: perfWorkerName('timerWorker') });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./TimerWorker.worker${ext}`, import.meta.url, perfWorkerName('timerWorker'));
    }
}
